import React, { useCallback, useState } from 'react';
import { WarningRounded as WarningRoundedIcon } from '@mui/icons-material';
import { Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { COLOR } from '@src/styles/color';
import CommonButton from '@src/new-components/Button';
import Tooltip from '@src/components/Tooltip';
import { countTextLength } from '@src/services/tts';
import { useSelector } from 'react-redux';
import { FEATURE } from '@src/constants/package';
import {
  PROJECT_PRODUCT,
  REWRITE_CREDIT_PER_CHAR,
} from '@src/constants/project';
import { MOE_SURFACE } from '@src/constants/moengage';
import useTotalCredits from '@src/hooks/useTotalCredits';
import useIsAutoSaveOverLimit from '@src/hooks/useIsAutoSaveOverLimit';
import {
  StyledWarningButton,
  StyledPopoverContent,
  StyledPopoverHeader,
  StyledPopoverTitle,
  StyledPopoverRow,
  StyledPopoverLabel,
  StyledPopoverValue,
  StyledCurrentValue,
  StyledMaxValue,
  StyledDot,
  StyledPopoverDescription,
  StyledBtnRow,
} from './index.style';
import PopupUpPkgUseRewrite from '../PopupUpPkgUseRewrite';

const RecommendedCharWarning = ({
  text,
  currentChars = 0,
  maxRecommended = 0,
  duration = 0,
  rewriteEnabled = false,
  onRewrite,
  loadingRewriteProject = false,
  onOpenPopupNotEnoughCredits,
  // The block's own synthesis, passed down instead of read from the store: the
  // project-wide `isProjectSynthesizing` flag stays false through the per-block
  // create-audio flow, so reading it here left this button live while the
  // block's audio was being made.
  isSynthesizing = false,
}) => {
  const { user } = useSelector((state) => state.auth);
  // Over the character limit the project can't be auto-saved, so block rewrite.
  const isAutoSaveOverLimit = useIsAutoSaveOverLimit();
  const [openTooltip, setOpenTooltip] = useState(false);
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPopupUpPkgUseRewrite, setOpenPopupUpPkgUseRewrite] =
    useState(false);

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);
  const { totalCredits } = useTotalCredits();
  const open = Boolean(anchorEl);
  const formattedDuration = `~ ${duration?.toFixed(1)}`;

  const costsCredit = Math.ceil(
    REWRITE_CREDIT_PER_CHAR * countTextLength(text),
  );
  const costsCreditTooltip = t('costsCredit', {
    value: costsCredit?.toLocaleString(language),
  });

  const handleCheckFeature = () => {
    setOpenTooltip(false);
    if (totalCredits < costsCredit) {
      onOpenPopupNotEnoughCredits();
      return;
    }
    const features = user?.studioUsageOptions?.features?.includes(
      FEATURE.AI_REWRITE,
    );

    if (!features) {
      setOpenPopupUpPkgUseRewrite(true);
      return;
    }
    handleClose();
    onRewrite();
  };

  return (
    <div onMouseLeave={handleClose}>
      <StyledWarningButton onMouseEnter={handleOpen}>
        <WarningRoundedIcon
          sx={{ color: COLOR.textWarning, fontSize: '24px' }}
        />
      </StyledWarningButton>
      {open && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
          disableRestoreFocus
          sx={{ pointerEvents: 'none' }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
              width: '320px',
              pointerEvents: 'auto',
            },
          }}
        >
          <StyledPopoverContent>
            <StyledPopoverHeader>
              <StyledPopoverTitle>
                {t('recommendedCharWarningTitle')}
              </StyledPopoverTitle>
              <StyledPopoverRow>
                <StyledPopoverLabel>{t('characters')}:</StyledPopoverLabel>
                <StyledPopoverValue>
                  {t('currentLabel')}{' '}
                  <StyledCurrentValue>
                    {currentChars?.toLocaleString(language)}
                  </StyledCurrentValue>
                </StyledPopoverValue>
                <StyledDot />
                <StyledPopoverValue>
                  {t('maxLabel')}{' '}
                  <StyledMaxValue>
                    {maxRecommended?.toLocaleString(language)}
                  </StyledMaxValue>
                </StyledPopoverValue>
              </StyledPopoverRow>
              <StyledPopoverRow>
                <StyledPopoverLabel>{t('duration')}:</StyledPopoverLabel>
                <StyledPopoverValue>
                  {formattedDuration} {t('seconds')}
                </StyledPopoverValue>
              </StyledPopoverRow>
            </StyledPopoverHeader>
            <StyledPopoverDescription>
              {t('recommendedCharWarningDescription')}
            </StyledPopoverDescription>
            {rewriteEnabled && (
              <Tooltip
                open={openTooltip && !loadingRewriteProject}
                onMouseEnter={() => setOpenTooltip(true)}
                onMouseLeave={() => setOpenTooltip(false)}
                title={costsCreditTooltip}
                arrow
                placement="top"
              >
                <StyledBtnRow>
                  <CommonButton
                    variant="outlined"
                    startIcon={
                      <AutoAwesomeRoundedIcon
                        sx={{ width: 24, height: 24, color: COLOR.blueV2 }}
                      />
                    }
                    textProps={{
                      variantText: 'body-14-semibold',
                      color: COLOR.darkBlue,
                    }}
                    customStyle={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '8px',
                    }}
                    disabled={
                      loadingRewriteProject ||
                      isSynthesizing ||
                      isAutoSaveOverLimit
                    }
                    onClick={handleCheckFeature}
                  >
                    {t('aiReWrite')}
                  </CommonButton>
                </StyledBtnRow>
              </Tooltip>
            )}
          </StyledPopoverContent>
        </Popover>
      )}
      <PopupUpPkgUseRewrite
        open={openPopupUpPkgUseRewrite}
        onClose={() => setOpenPopupUpPkgUseRewrite(false)}
        featureKey={FEATURE.AI_REWRITE}
        surface={MOE_SURFACE.PROJECT_EDITOR}
        product={PROJECT_PRODUCT.DUBBING}
      />
    </div>
  );
};

export default RecommendedCharWarning;
